import { Button, Card, Group, Stack, Text, TextInput } from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { api, type Customer } from "../lib/api";
import { EmptyState, InlineError, LoadingSkeleton, RecordCard } from "./ui";

/**
 * Select a customer from the managed master list (Q6).
 *
 * Field staff cannot create customers here, and that is deliberate: the Store
 * Manager owns the list, so the same buyer cannot arrive three different ways
 * and fragment their own service history. If a customer is genuinely missing,
 * the message below says who can add them rather than offering a shortcut.
 *
 * Search is debounced and matches phone or name, because those are the two
 * things an agent standing at a gate actually has.
 */
export function CustomerPicker({
  value,
  onChange,
}: {
  value?: Customer;
  onChange: (customer: Customer | undefined) => void;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Customer[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [touched, setTouched] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (value) return; // Nothing to search for once one is chosen.
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(async () => {
      setBusy(true);
      setError(undefined);
      try {
        const { customers } = await api.customers(query.trim() || undefined);
        setResults(customers);
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : "Could not load customers.");
      } finally {
        setBusy(false);
        setTouched(true);
      }
    }, 300);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [query, value]);

  if (value) {
    return (
      <Card withBorder radius="md" padding="md" bg="indigo.0">
        <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
          <Stack gap={2} style={{ minWidth: 0 }}>
            <Text fz="lg" fw={600} c="gray.9">
              {value.name}
            </Text>
            {value.phone && (
              <Text fz="md" c="gray.7" className="num">
                {value.phone}
              </Text>
            )}
            {value.address && (
              <Text fz="sm" c="gray.6">
                {value.address}
              </Text>
            )}
          </Stack>
          <Button
            variant="default"
            size="sm"
            onClick={() => {
              onChange(undefined);
              setQuery("");
            }}
          >
            Change
          </Button>
        </Group>
      </Card>
    );
  }

  return (
    <Stack gap="sm">
      <TextInput
        type="search"
        inputMode="search"
        placeholder="Search by phone or name"
        leftSection={<IconSearch size={16} stroke={1.8} />}
        value={query}
        onChange={(event) => setQuery(event.currentTarget.value)}
        aria-label="Search customers"
      />

      {busy && <LoadingSkeleton variant="list" count={3} />}
      {error && <InlineError>{error}</InlineError>}

      {!busy && touched && results.length === 0 && (
        <EmptyState
          title="No matching customer"
          description="The customer list is managed by the Store Manager — ask them to add this customer before placing the order."
        />
      )}

      {!busy &&
        results.map((customer) => (
          <RecordCard
            key={customer.id}
            primary={customer.name}
            secondary={customer.phone ?? undefined}
            meta={customer.address ?? undefined}
            onClick={() => onChange(customer)}
          />
        ))}
    </Stack>
  );
}
